// frontend/shared-ui/src/forms/schemas/odf.js

// ─── Type classification ─────────────────────────────────────────────
// Every productType maps to a "family". Families decide which groups show.
const RACK_ODF_TYPES = [
    'Rack ODF',
    '19" Rack ODF',
    'Rack Mount Patch Panel',
];

const WALL_MOUNT_TYPES = [
    'Wall Mount Box',
    'Wall Mount Enclosure',
    'Wall Mount Patch Panel',
];

const FTB_TYPES = [
    'Fiber Terminal Box (FTB)',
    'Terminal Box (FTB)',
    'FTB',
    'Mini Terminal Box',
];

const SPLICE_ENCLOSURE_TYPES = [
    'Splice Enclosure',
    'Cross Connect Box',
    'Distribution Box',
];

const isOneOf = (v, list) => list.includes(v && v.productType);
const isRackOdf = (v) => isOneOf(v, RACK_ODF_TYPES);
const isWallMount = (v) => isOneOf(v, WALL_MOUNT_TYPES);
const isFTB = (v) => isOneOf(v, FTB_TYPES);
const isSpliceEnclosure = (v) => isOneOf(v, SPLICE_ENCLOSURE_TYPES);

// ─── Static option sets ─────────────────────────────────────────────
const CONNECTOR_CODES = { SC: 'SC', LC: 'LC', FC: 'FC', ST: 'ST' };
const POLISH_CODES = { APC: 'APC', UPC: 'UPC' };
const LOADING_CODES = {
    'Fully Loaded': 'FL',
    'Half Loaded': 'HL',
    'Unloaded / Empty': 'UL',
};
const MOUNT_CODES = {
    '19-Inch Rack Mount': '19R',
    'Wall Mount': 'WM',
    'DIN Rail': 'DR',
    'Pole Mount': 'PM',
    'Desktop / Bench': 'DT',
};
const ADAPTER_CODES = { Simplex: 'SX', Duplex: 'DX', Quad: 'QX' };

const PRODUCT_TYPE_CODES = {
    'Rack ODF': 'RODF',
    '19" Rack ODF': 'RODF',
    'Rack Mount Patch Panel': 'RPP',
    'Wall Mount Box': 'WMB',
    'Wall Mount Enclosure': 'WME',
    'Wall Mount Patch Panel': 'WPP',
    'Fiber Terminal Box (FTB)': 'FTB',
    'Terminal Box (FTB)': 'FTB',
    'FTB': 'FTB',
    'Mini Terminal Box': 'MFTB',
    'Splice Enclosure': 'SPEN',
    'Cross Connect Box': 'CCB',
    'Distribution Box': 'DSB',
};

const MATERIAL_OPTIONS_COMMON = [
    { value: 'Cold-rolled Steel', label: 'Cold-rolled Steel' },
    { value: 'Stainless Steel', label: 'Stainless Steel' },
    { value: 'Aluminum', label: 'Aluminum' },
    { value: 'ABS Plastic', label: 'ABS Plastic' },
    { value: 'PC (Polycarbonate)', label: 'PC (Polycarbonate)' },
    { value: 'PP (Polypropylene)', label: 'PP (Polypropylene)' },
];

const IP_RATING_OPTIONS = [
    { value: 'IP20', label: 'IP20 (indoor dry)' },
    { value: 'IP44', label: 'IP44' },
    { value: 'IP54', label: 'IP54 (dust/splash)' },
    { value: 'IP65', label: 'IP65 (dust jet water)' },
    { value: 'IP66', label: 'IP66' },
    { value: 'IP67', label: 'IP67 (immersion)' },
    { value: 'IP68', label: 'IP68 (continuous immersion)' },
];

// ─── Schema ──────────────────────────────────────────────────────────
export default {
    slug: 'odf',
    label: 'ODF / Patch Panels / FTB',
    defaultUnit: 'pcs',
    costDefaults: {
        avgVolumeCbm: 0.025,
        suggestedMarkupPct: 40,
        freightSensitivity: 'high',
        note: 'Varies by port count — measure the specific model',
    },

    groups: [
        // ──────────────────────────────────────────────────────────────
        // 1. BASIC — always shown, includes Product Type + capacity
        // ──────────────────────────────────────────────────────────────
        {
            id: 'basic',
            label: 'Basic',
            columns: 2,
            fields: [
                {
                    key: 'productType',
                    label: 'Product Type',
                    type: 'select',
                    required: true,
                    options: [
                        'Rack ODF',
                        'Rack Mount Patch Panel',
                        'Wall Mount Box',
                        'Wall Mount Enclosure',
                        'Wall Mount Patch Panel',
                        'Fiber Terminal Box (FTB)',
                        'Mini Terminal Box',
                        'Splice Enclosure',
                        'Cross Connect Box',
                        'Distribution Box',
                    ],
                },
                {
                    key: 'capacity',
                    label: 'Capacity (cores)',
                    type: 'quick-qty',
                    required: true,
                    min: 1,
                    unitOptions: ['core', 'cores'],
                    presets: [4, 8, 12, 24, 48, 96, 144, 288, 576],
                    hint: 'Total fiber cores this unit can terminate',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 2. OPTICAL — always shown
        // ──────────────────────────────────────────────────────────────
        {
            id: 'optical',
            label: 'Optical',
            columns: 3,
            fields: [
                {
                    key: 'connectorType',
                    label: 'Connector Type',
                    type: 'select',
                    required: true,
                    options: ['SC', 'LC', 'FC', 'ST'],
                },
                {
                    key: 'polishType',
                    label: 'Polish',
                    type: 'select',
                    required: true,
                    options: [
                        { value: 'APC', label: 'APC (Green)' },
                        { value: 'UPC', label: 'UPC (Blue)' },
                    ],
                },
                {
                    key: 'adapterType',
                    label: 'Adapter Type',
                    type: 'select',
                    required: true,
                    options: ['Simplex', 'Duplex', 'Quad'],
                },
                {
                    key: 'fiberMode',
                    label: 'Fiber Mode',
                    type: 'select',
                    required: true,
                    options: [
                        { value: 'Singlemode', label: 'Singlemode (OS2)' },
                        { value: 'Multimode OM3', label: 'Multimode (OM3)' },
                        { value: 'Multimode OM4', label: 'Multimode (OM4)' },
                    ],
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 3. RACK-SPECIFIC — only when productType is a rack unit
        // ──────────────────────────────────────────────────────────────
        {
            id: 'rack',
            label: 'Rack Specifications',
            columns: 2,
            when: isRackOdf,
            fields: [
                {
                    key: 'unitHeight',
                    label: 'Rack Unit Height',
                    type: 'select',
                    options: ['1U', '2U', '3U', '4U', '6U', '9U'],
                },
                {
                    key: 'mountType',
                    label: 'Mounting',
                    type: 'select',
                    options: [
                        '19-Inch Rack Mount',
                        'Wall Mount',
                        'DIN Rail',
                        'Pole Mount',
                    ],
                },
                {
                    key: 'drawerType',
                    label: 'Drawer Type',
                    type: 'select',
                    options: ['Sliding Drawer', 'Fixed', 'Swing-out'],
                },
                {
                    key: 'spliceTrayCount',
                    label: 'Splice Trays',
                    type: 'number',
                    min: 0,
                    hint: 'Number of splice trays included',
                },
                {
                    key: 'loadingStatus',
                    label: 'Loading Status',
                    type: 'select',
                    options: ['Fully Loaded', 'Half Loaded', 'Unloaded / Empty'],
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 4. WALL MOUNT-SPECIFIC — only when productType is a wall mount
        // ──────────────────────────────────────────────────────────────
        {
            id: 'wallMount',
            label: 'Wall Mount Specifications',
            columns: 2,
            when: isWallMount,
            fields: [
                {
                    key: 'mountType',
                    label: 'Mounting',
                    type: 'select',
                    options: ['Wall Mount', 'Pole Mount', 'Desktop / Bench'],
                },
                {
                    key: 'ipRating',
                    label: 'IP Rating',
                    type: 'select',
                    options: IP_RATING_OPTIONS,
                },
                {
                    key: 'lockable',
                    label: 'Lockable',
                    type: 'boolean',
                    hint: 'Does the door/lid have a lock?',
                },
                {
                    key: 'doorType',
                    label: 'Door / Lid Type',
                    type: 'select',
                    options: [
                        'Hinged',
                        'Sliding',
                        'Screw-on',
                        'Tool-less Snap',
                        'No Door',
                    ],
                },
                {
                    key: 'spliceTrayCount',
                    label: 'Splice Trays',
                    type: 'number',
                    min: 0,
                },
                {
                    key: 'loadingStatus',
                    label: 'Loading Status',
                    type: 'select',
                    options: ['Fully Loaded', 'Half Loaded', 'Unloaded / Empty'],
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 5. FTB-SPECIFIC — only when productType is an FTB
        // ──────────────────────────────────────────────────────────────
        {
            id: 'ftb',
            label: 'FTB Specifications',
            columns: 2,
            when: isFTB,
            fields: [
                {
                    key: 'ipRating',
                    label: 'IP Rating',
                    type: 'select',
                    options: IP_RATING_OPTIONS,
                },
                {
                    key: 'installationType',
                    label: 'Installation',
                    type: 'select',
                    options: [
                        'Indoor Wall Mount',
                        'Outdoor Wall Mount',
                        'Pole Mount',
                        'Desktop',
                    ],
                },
                {
                    key: 'cableEntry',
                    label: 'Cable Entry',
                    type: 'select',
                    options: [
                        'Bottom (drop cable)',
                        'Rear',
                        'Top + Bottom',
                        'Multi-directional',
                    ],
                },
                {
                    key: 'spliceTrayCount',
                    label: 'Splice Trays',
                    type: 'number',
                    min: 0,
                },
                {
                    key: 'loadingStatus',
                    label: 'Loading Status',
                    type: 'select',
                    options: ['Fully Loaded', 'Half Loaded', 'Unloaded / Empty'],
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 6. SPLICE ENCLOSURE-SPECIFIC
        // ──────────────────────────────────────────────────────────────
        {
            id: 'spliceEnclosure',
            label: 'Splice Enclosure Specifications',
            columns: 2,
            when: isSpliceEnclosure,
            fields: [
                {
                    key: 'ipRating',
                    label: 'IP Rating',
                    type: 'select',
                    options: IP_RATING_OPTIONS,
                },
                {
                    key: 'sealingType',
                    label: 'Sealing Type',
                    type: 'select',
                    options: ['Heat-Shrink', 'Mechanical', 'Cold Seal', 'Gel Seal'],
                },
                {
                    key: 'cablePorts',
                    label: 'Cable Ports',
                    type: 'number',
                    min: 0,
                },
                {
                    key: 'reEnterable',
                    label: 'Re-enterable',
                    type: 'boolean',
                },
            ],
        },

        // ──────────────────────────────────────────────────────────────
        // 7. PHYSICAL — always shown (material list adapts per type)
        // ──────────────────────────────────────────────────────────────
        {
            id: 'physical',
            label: 'Physical & Dimensions',
            columns: 3,
            fields: [
                {
                    key: 'material',
                    label: 'Material',
                    type: 'select',
                    options: MATERIAL_OPTIONS_COMMON,
                },
                {
                    key: 'color',
                    label: 'Color',
                    type: 'select',
                    options: [
                        'Grey (RAL 7035)',
                        'Black (RAL 9005)',
                        'White',
                        'Beige',
                        'Custom',
                    ],
                },
                {
                    key: 'height_mm',
                    label: 'Height',
                    type: 'number',
                    unit: 'mm',
                },
                {
                    key: 'width_mm',
                    label: 'Width',
                    type: 'number',
                    unit: 'mm',
                },
                {
                    key: 'depth_mm',
                    label: 'Depth',
                    type: 'number',
                    unit: 'mm',
                },
                {
                    key: 'weight_g',
                    label: 'Weight',
                    type: 'number',
                    unit: 'g',
                },
            ],
        },
    ],

    // ─── SKU generation ─────────────────────────────────────────────────
    buildSku(v) {
        // capacity is a number from QuickQuantityField (or a legacy string)
        const capacityDigits =
            v.capacity != null && v.capacity !== ''
                ? String(v.capacity).replace(/[^0-9]/g, '')
                : '';

        const typeCode = PRODUCT_TYPE_CODES[v.productType] || 'ODF';
        const connector = CONNECTOR_CODES[v.connectorType] || v.connectorType || '';
        const polish = POLISH_CODES[v.polishType] || v.polishType || '';
        const adapter = ADAPTER_CODES[v.adapterType] || '';
        const loading = LOADING_CODES[v.loadingStatus] || '';
        const color = v.color?.slice(0, 4);
        const mount =
            v.mountType && MOUNT_CODES[v.mountType] ? MOUNT_CODES[v.mountType] : '';

        const parts = [
            'ODF',
            typeCode,
            capacityDigits,
            connector,
            polish,
            loading,
            adapter,
            v.unitHeight || '',   // only set for rack types
            mount,
            color
        ].filter(Boolean);

        return parts.join('-').replace(/-+/g, '-').toUpperCase();
    },

    // ─── Name generation ────────────────────────────────────────────────
    buildName(v) {
        const bits = [];

        // Capacity first: "144-Core"
        if (v.capacity != null && v.capacity !== '') {
            const n = String(v.capacity).replace(/[^0-9]/g, '');
            if (n) bits.push(`${n}-Core`);
        }

        // Product type
        if (v.productType) bits.push(v.productType);

        // Connector/polish
        if (v.connectorType) {
            const conn = v.polishType
                ? `${v.connectorType}/${v.polishType}`
                : v.connectorType;
            if (v.adapterType) bits.push(`${conn} ${v.adapterType}`);
            else bits.push(conn);
        }

        // Rack: unit height + mount
        if (v.unitHeight) bits.push(v.unitHeight);

        // Loading state
        if (v.loadingStatus) bits.push(v.loadingStatus);

        if (v.color) bits.push(v.color);

        // Mount / installation
        // if (v.mountType) bits.push(v.mountType);
        // if (v.installationType) bits.push(v.installationType);

        // Enclosure extras
        // if (v.ipRating) bits.push(v.ipRating);
        if (v.sealingType) bits.push(`(${v.sealingType})`);
        // if (v.material) bits.push(`— ${v.material}`);

        // Dimensions tail
        // const dims = [];
        // if (v.height_mm) dims.push(`H${v.height_mm}`);
        // if (v.width_mm) dims.push(`W${v.width_mm}`);
        // if (v.depth_mm) dims.push(`D${v.depth_mm}`);

        const out = bits.filter(Boolean).join(' ').trim();
        // if (dims.length) return `${out} (${dims.join(' × ')}mm)`;
        return out;
    },

    // ─── Detail view ────────────────────────────────────────────────────
    attributeSchema: [
        { key: 'productType', label: 'Product Type' },
        { key: 'capacity', label: 'Capacity', unit: 'cores' },
        { key: 'connectorType', label: 'Connector Type' },
        { key: 'polishType', label: 'Polish' },
        { key: 'adapterType', label: 'Adapter Type' },
        { key: 'fiberMode', label: 'Fiber Mode' },
        { key: 'loadingStatus', label: 'Loading Status' },
        { key: 'unitHeight', label: 'Rack Unit Height' },
        { key: 'mountType', label: 'Mounting' },
        { key: 'drawerType', label: 'Drawer Type' },
        { key: 'installationType', label: 'Installation' },
        { key: 'ipRating', label: 'IP Rating' },
        { key: 'doorType', label: 'Door / Lid Type' },
        { key: 'lockable', label: 'Lockable' },
        { key: 'cableEntry', label: 'Cable Entry' },
        { key: 'sealingType', label: 'Sealing Type' },
        { key: 'cablePorts', label: 'Cable Ports' },
        { key: 'reEnterable', label: 'Re-enterable' },
        { key: 'spliceTrayCount', label: 'Splice Trays' },
        { key: 'color', label: 'Color' },
        { key: 'material', label: 'Material' },
        { key: 'height_mm', label: 'Height', unit: 'mm' },
        { key: 'width_mm', label: 'Width', unit: 'mm' },
        { key: 'depth_mm', label: 'Depth', unit: 'mm' },
        { key: 'weight_g', label: 'Weight', unit: 'g' },
    ],
};